import { useOIDCAuth } from '@/composables/useOIDCAuth';
import { AuthToken } from '@/types';
import { jwtDecode } from 'jwt-decode';
import { RouteLocationNormalized, Router } from 'vue-router';

/**
 * ページアクセス権限をチェックする
 * @param to 遷移先のルート
 * @param authStore 認証ストア
 * @param next ナビゲーション関数
 * @returns 権限がある場合true、ない場合false
 */
function checkPageAccess(to: RouteLocationNormalized, authStore: any, next: Function): boolean {
  const toPath = to.path.replace(/\/+$/, ''); // 末尾のスラッシュを削除
  console.log('アクセス先パス:', toPath);

  // 認証不要ページや特殊ページのチェック
  const publicPaths = ['/login', '/logout', '/systemerror', '/info'];
  if (
    publicPaths.includes(toPath.toLowerCase()) ||
    toPath === '' ||
    toPath === '/' ||
    toPath.toLowerCase() === '/toppage'
  ) {
    return true;
  }

  // ページキーを生成（先頭のスラッシュを除去して大文字化）
  const pageKey = toPath.replace(/^\//, '').toUpperCase();
  const accessPrivilege = authStore.getAccessPrivilegeByPageKey(pageKey);
  if (!accessPrivilege) {
    // 権限がない場合、TopPageへリダイレクト
    console.log(`ページ ${pageKey} への権限がありません。TopPageにリダイレクトします。`);
    next({ path: '/TopPage' });
    return false;
  }

  console.log(`ページ ${pageKey} へのアクセス権限確認: ${accessPrivilege}`);
  return true;
}

export function setupAuthGuard(router: Router) {
  router.beforeEach(async (to: RouteLocationNormalized, from: RouteLocationNormalized, next) => {
    // ルートが認証を必要とするかをチェック
    const requiresAuth = to.meta.requiresAuth !== false; // デフォルトはtrue
    if (!requiresAuth) {
      // 認証が不要なルートは直接通す
      next();
      return;
    }

    // Cognito認証コールバックの場合は直接通す（/loginページで処理される）
    const urlParams = new URLSearchParams(to.query as any);
    const hasAuthCallback = urlParams.has('code') || urlParams.has('error') || to.query.code || to.query.error;

    if (hasAuthCallback && to.path === '/login') {
      console.log('Cognito認証コールバックを検出、ログインページで処理');
      next();
      return;
    }

    // 認証が必要なルートに対して認証チェックを実行
    const { signIn, saveOriginalUrl, checkAuthStatus } = useOIDCAuth();

    // アプリケーションレベルの認証状態をまずチェック
    const authStore = await import('@/stores/auth').then((m) => m.useAuthStore());
    authStore.restoreFromStorage();

    // 新しいタブでも既存のOIDC認証が利用できるかチェック
    console.log('OIDC認証状態をチェック中...');
    const isOIDCAuthenticated = await checkAuthStatus();

    if (isOIDCAuthenticated) {
      console.log('OIDC認証有効、アクセス許可');
      // 権限チェックを実行
      const hasPageAccess = checkPageAccess(to, authStore, next);
      if (!hasPageAccess) {
        return;
      }
      next();
      return;
    }

    console.log('authStore.userInfo:', authStore.userInfo);

    // OIDC認証が無効な場合、アプリケーションレベルの認証状態をチェック
    const hasAppLevelAuth = authStore.userInfo.employeeName && authStore.token;

    if (!hasAppLevelAuth) {
      // アプリケーション・OIDC共に未認証の場合、ログインページにリダイレクト
      console.log('アプリケーション・OIDC共に未認証、ログインページにリダイレクト');
      next({ path: '/login', query: { redirect: to.fullPath } });
      return;
    }

    // アプリケーションレベルで認証済みの場合、OIDC状態もチェック
    const isCurrentlyAuthenticated = await checkAuthStatus();

    if (isCurrentlyAuthenticated) {
      // OIDC認証済み、アプリケーション状態を初期化
      const appStore = await import('@/stores/app').then((m) => m.useAppStore());
      if (appStore.init === false) {
        appStore.setInit(true);

        // トークンを検証
        const token = verifyAuthInfo(authStore.token);
        if (!token) {
          // トークンが無効、ログインページにリダイレクト
          next({ path: '/login', query: { message: 'ユーザー資格情報が確認できません。' } });
          return;
        }
      }
      // 权限チェックを実行
      const hasPageAccess = checkPageAccess(to, authStore, next);
      if (!hasPageAccess) {
        return;
      }
      next();
    } else {
      // OIDC認証が無効になっている場合、再認証
      console.log('OIDC認証が無効、再認証フローを開始');

      try {
        // 元のURL（/loginページを含む）を保存
        const fullUrl = window.location.origin + to.fullPath;
        saveOriginalUrl(fullUrl);

        // OIDC認証フローを開始
        await signIn();

        // signInはリダイレクトを実行するため、next()の呼び出しは不要
      } catch (error) {
        console.error('OIDC認証の开始に失敗:', error);
        // OIDC開始が失敗した場合、ログインページにフォールバック
        next({ path: '/login', query: { error: 'OIDC認証开始失敗' } });
      }
    }
  });
}

/**
 * JWTのデコードを行う
 * @param authInfo 認証JWT文字列
 * @returns
 */
function verifyAuthInfo(authInfo: string, checkValidTime = true): AuthToken | null {
  let token: AuthToken;
  try {
    token = jwtDecode<AuthToken>(authInfo);
  } catch (err) {
    console.log('トークンのデコード失敗');
    return null;
  }
  return token;
}
